import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { Box, CircularProgress, Grid, Pagination, Stack, Tooltip, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import BasicTabs from '../../common/basic-tabs/BasicTabs';
import DetailsTabs from './components/DetailsTab';
import DocumentsTabs from './components/DocumentsTabs';
import NotesTab from '../../common/notes-tab/NotesTab';
import FormUpload from '../../common/form-upload/FormUpload';
import UploadButton from '../../common/upload-button/UploadButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';

import styles from './EmployeeProfile.module.scss';
import CreateContactModal from '../../common/create-contact-modal/CreateContactModal';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchEmployees, selectEmployeeById } from '../../slices/employees/employeesSlice';
import { RootState } from '../../store';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import { ROUTES } from '../../common/constants/routes';
import { useDocuments } from '../document-vault/hooks/useDocuments';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';

const EmployeeProfile = () => {
  const dispatch = useAppDispatch();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [workflow, setWorkflow] = useState<string | null>(null);
  const company = useAppSelector(selectAllCompanies)[0];
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const { employeeId } = useParams();
  const parsedEmployeeId = Number(employeeId);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };
  const employee = useAppSelector((state: RootState) =>
    selectEmployeeById(state, parsedEmployeeId)
  );

  const [searchParams] = useSearchParams();
  const initialTab = Number(searchParams.get('initialTab')) || 0;
  const checkPermission = useCheckPermission();

  const {
    documents,
    documentsCount,
    currentPage,
    setCurrentPage,
    getRequestedPage,
    showWorkflowBreadcrumbs,
    setShowWorkflowBreadcrumbs,
    cardView,
    setCardView,
  } = useDocuments('employment', workflow, parsedEmployeeId);
  const combinedDocuments = [...documents].slice().sort((a, b) => sortByDate(a, b));

  // Items per page - adjust this value according to your API
  const itemsPerPage = 20;
  // Calculate total pages
  const totalPages = Math.ceil(documentsCount / itemsPerPage);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getRequestedPage(page);
  };

  useEffect(() => {
    if (company?.id) {
      dispatch(fetchEmployees({ company_id: company.id }));
    }
  }, [company?.id, dispatch]);

  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  // Actually handles both open & close
  const handleUploadOpen = () => {
    setIsUploadOpen((prev) => !prev);
  };

  /*useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (
        targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
        targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
      ) {
        return;
      }
      if (
        formUploadRef.current &&
        !formUploadRef.current.contains(event.target as Node) &&
        uploadButtonRef.current &&
        !uploadButtonRef.current.contains(event.target as Node)
      ) {
        setIsUploadOpen(false);
      }
    };

    if (isUploadOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUploadOpen]);*/

  const toogleShowAddlModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  if (!company || !employee) {
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 104px)',
        }}
      >
        <CircularProgress color='primary' size={50} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ mt: '-54px' }}>
      <Grid size={{ xs: 12 }}>
        <Box className={styles.breadcrumbs}>
          <Link className={styles.documentViewText} to={ROUTES.EMPLOYEE_PROFILES}>
            {t('employeeProfiles.header')}
          </Link>
          <Typography variant='body2' className={styles.separator}>
            ›
          </Typography>
          <Typography variant='body2' className={styles.documentViewText}>
            {employee.full_name}
          </Typography>
        </Box>
        <Stack spacing={2}>
          <Typography variant='h1' component='h1' className={styles.page_title}>
            {employee.full_name}
          </Typography>
          <Stack
            direction='row'
            spacing={2}
            justifyContent={'space-between'}
            alignItems={'center'}
            style={{ marginTop: '8px' }}
          >
            <Box display='flex' alignItems={'center'} gap={'8px'}>
              <Box ref={uploadButtonRef}>
                {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_CREATE) &&
                checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_EMPLOYEE_MANAGE) &&
                checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_ASSIGN_PROFILE) ? (
                  <UploadButton
                    onClick={handleUploadOpen}
                    buttonText={t('buttons.uploadFiles')}
                    isArrowDown={false}
                  />
                ) : (
                  <Tooltip title={t('messages.contactAdministrator')} arrow placement='top'>
                    <UploadButton
                      onClick={handleUploadOpen}
                      buttonText={t('buttons.uploadFiles')}
                      isArrowDown={false}
                      disabled={true}
                    />
                  </Tooltip>
                )}
              </Box>
              {isUploadOpen && (
                <Box className={styles.form_box} alignSelf={'flex-start'} ref={formUploadRef}>
                  <FormUpload
                    onCancel={handleUploadClose}
                    categoryValue='employment'
                    employees={[employee]}
                    workflow={workflow}
                  />
                </Box>
              )}
              <PreviewTooltip title={<Preview />} arrow placement='top'>
                <InfoIcon color='primary' fontSize='small' />
              </PreviewTooltip>
            </Box>
            <Box display={'flex'} alignItems={'center'} gap={'8px'}>
              <span style={{ fontSize: '14px' }}>{t('labels.documentsView')}: </span>
              {documents.length > 0 && workflow && (
                <Box className='pagination-block' sx={{ display: 'flex', justifyContent: 'end' }}>
                  <Typography className='pagination-pages-value' variant='caption'>
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    siblingCount={0}
                    boundaryCount={0}
                    sx={{
                      '& .MuiPagination-ul': { gap: 0 },
                    }}
                  />
                </Box>
              )}
              <Box className={styles.card_list_view_container}>
                <button
                  className={`${styles.variant_btn} + ${cardView ? styles.variant_btn_active : ''}`}
                  onClick={() => setCardView(true)}
                >
                  <DashboardIcon />
                  <span>{t('buttons.card')}</span>
                </button>
                <button
                  onClick={() => {
                    setCardView(false);
                  }}
                  disabled={!!workflow && showWorkflowBreadcrumbs}
                  className={`${styles.variant_btn} + ${
                    !cardView ? styles.variant_btn_active : ''
                  }`}
                >
                  <BallotIcon />
                  <span>{t('buttons.list')}</span>
                </button>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12 }} className={styles.grid_margin_bottom}>
        <BasicTabs
          tabs={[
            /*t('businessProfile.allTab'),*/
            t('businessProfile.documentsTabs'),
            t('businessProfile.detailsTab'),
            t('businessProfile.notesTab'),
          ]}
          tabsChangeSideEffect={() => setWorkflow(null)}
          tabContents={[
            /*<AllTabs
                                      employee={employee}
                                      company={company}
                                      documents={combinedDocuments}
                                      key='all'
                                    />,*/
            <DocumentsTabs
              documents={combinedDocuments}
              workflow={workflow}
              setWorkflow={setWorkflow}
              cardView={cardView}
              showWorkflowBreadcrumbs={showWorkflowBreadcrumbs}
              setShowWorkflowBreadcrumbs={setShowWorkflowBreadcrumbs}
              key='documents'
            />,
            <DetailsTabs employee={employee} company={company} key='details' />,
            <NotesTab employee={employee} key='notes' />,
          ]}
          initialTab={initialTab}
        />
      </Grid>
      {isModalOpen && (
        <CreateContactModal
          companyId={company.id}
          open={isModalOpen}
          onClose={() => toogleShowAddlModal()}
        />
      )}
    </Grid>
  );
};

export default EmployeeProfile;
